import { Lockloader } from '@/assets/images';
import logoUrl from '@/assets/images/logo.svg';
import logoDarkUrl from '@/assets/images/logo-dark.svg';
import { AppIcon, AppText } from 'components';
import PropTypes from 'prop-types';
import { Suspense, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import storage from '@/shared/utils/storage';
import './sync.scss';

const SyncUI = ({ isSyncing, isSyncComplete, progress = 0 }) => {
   const [shouldShow, setShouldShow] = useState(true);
   const [isHiding, setIsHiding] = useState(false);
   const isDark = document.body.classList.contains('dark-body');
   const activeLogo = isDark ? logoDarkUrl : logoUrl;

   useEffect(() => {
      let hideTimer;
      let removeTimer;

      if (isSyncComplete) {
         // Show 100% for 500ms before starting fade out
         hideTimer = setTimeout(() => {
            setIsHiding(true);
            // Actually remove from DOM after fade animation completes
            removeTimer = setTimeout(() => {
               setShouldShow(false);
            }, 300); // Match CSS transition duration
         }, 500);
      } else if (!isSyncComplete) {
         setShouldShow(true);
         setIsHiding(false);
      }

      return () => {
         if (hideTimer) clearTimeout(hideTimer);
         if (removeTimer) clearTimeout(removeTimer);
      };
   }, [isSyncComplete, progress]);

   // Don't render anything if sync is complete and hide animation finished
   if (!shouldShow) {
      return null;
   }

   return (
      <div className="sync-status-container ct-flexCenter">
         <div
            className={`sync-status-progress ${isSyncing || progress === 100 ? 'syncing' : 'complete'} ${isHiding ? 'hiding' : ''}`}
         >
            {(isSyncing || isSyncComplete) && (
               <div className="progress_bartop ct-flex ct-fcol ct-aiCenter ct-jcCenter">
                  <div className="progress_contain ct-flex ct-fcol ct-aiCenter ct-gap12">
                     <img src={activeLogo} alt="MirrorFly Teams" className="logo-img" />
                     <div className="progress-bar">
                        <div className="prog-status" style={{ width: `${Math.min(progress, 100)}%` }}></div>
                     </div>
                     <AppText className="progress-text ct-flex ct-aiCenter ct-gap8">
                        <AppIcon icon={<Lockloader />} className="load_lock" />
                        Syncing your conversations…
                     </AppText>
                  </div>
               </div>
            )}
         </div>
      </div>
   );
};

SyncUI.propTypes = {
   isSyncing: PropTypes.bool,
   isSyncComplete: PropTypes.bool,
   progress: PropTypes.number,
};

// Standalone fallback for Suspense — invisible, SyncUI overlay handles all visuals
const PageLoader = () => <div style={{ height: '100vh', background: 'transparent' }} />;

const Sync = ({ children }) => {
   const syncProgress = useSelector(state => state.syncProgressData?.progress || 0);
   const username = storage.getItem('username');
   const authFlow = storage.getItem('auth_flow');
   const hasUser = !!username;
   // Sync should only block rendering if we are on the dashboard flow
   // For registration/profile update flows, we allow children to render immediately
   const isDashboardFlow = authFlow === 'dashboard';
   const isSyncComplete = !hasUser || !isDashboardFlow || syncProgress >= 100;
   const isSyncing = hasUser && isDashboardFlow && syncProgress > 0 && syncProgress < 100;
   const displayProgress = hasUser && isDashboardFlow ? syncProgress : 0;

   return (
      <>
         {/* Single SyncUI instance — always shows initially as splash/loader */}
         {/* position:fixed + z-index:9999 covers everything, no flicker */}
         <SyncUI isSyncing={isSyncing} isSyncComplete={isSyncComplete} progress={displayProgress} />
         <Suspense fallback={<PageLoader />}>{isSyncComplete ? children : null}</Suspense>
      </>
   );
};

Sync.propTypes = {
   children: PropTypes.node,
};

export { SyncUI };
export default Sync;
